<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import BaseButton from '../_base-button.vue'
import BaseIcon from '../_base-icon.vue'

const props = defineProps({
    data: {
        type: Object,
        default: () => ({}),
    },
})

const emit = defineEmits(['close'])

const router = useRouter()

const headline = computed(() => props.data.headline)

const description = computed(() => props.data.description)

const primaryAction = computed(() => props.data.primaryAction)

const secondaryAction = computed(() => props.data.secondaryAction)

function close() {
    emit('close')
}

function handlePrimaryAction() {
    if (primaryAction.value.route) {
        router.push(primaryAction.value.route)
    }
    emit('close', 'primary')
}

function handleSecondaryAction() {
    if (!secondaryAction.value) return

    if (secondaryAction.value.route) {
        router.push(secondaryAction.value.route)
    }
    emit('close', 'secondary')
}
</script>

<template>
    <div class="auth-prompt-modal">
        <button
            class="close-button"
            aria-label="Close modal"
            @click="close"
        >
            <BaseIcon
                class="close-icon"
                name="close"
            />
        </button>

        <div class="content">
            <h2 class="headline">{{ headline }}</h2>
            <p
                v-if="description"
                class="description"
            >
                {{ description }}
            </p>

            <div class="actions">
                <BaseButton
                    variant="primary"
                    size="l"
                    @click="handlePrimaryAction"
                >
                    {{ primaryAction.text }}
                </BaseButton>

                <button
                    v-if="secondaryAction"
                    class="secondary-link"
                    @click="handleSecondaryAction"
                >
                    {{ secondaryAction.text }}
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
@use '@design' as *;

.auth-prompt-modal {
    position: relative;
    width: 90vw;
    max-width: 710px;
    padding: $s-h $s-h $s-xxxl;
    border: none;
    border-radius: $radius-12;

    @include elevationDialog;

    @include mobileOnly {
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: calc(100vw - $s-xxl);
        max-width: none;
        height: auto;
        padding: $s-xxl 30px 36px;
        margin: $s-l;
        border-radius: $radius-16;
    }
}

.close-button {
    position: absolute;
    top: $s-l;
    right: $s-l;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    padding: $s-xxs;
    color: $c-modal-close;
    cursor: pointer;
    background: transparent;
    border: none;
    border-radius: 50%;

    @include transition((color, background-color), fast);

    .close-icon {
        width: $s-l;
        height: $s-l;
    }

    &:hover {
        color: $c-modal-close-hover;
        background: $c-modal-close-hover-bg;
    }

    @include mobileOnly {
        top: $s-l;
        right: $s-l;
    }
}

.content {
    display: flex;
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
    text-align: left;
}

.headline {
    margin: 0;
    margin-bottom: 140px;
    font-family: $system-default-font-family;
    font-size: 35px;
    font-weight: 700;
    line-height: 1.2;
    color: $c-white;
    text-align: left;
    letter-spacing: -0.03em;
    white-space: pre-wrap;

    @include mobileOnly {
        max-width: 280px;
        margin-top: $s-xl;
        margin-bottom: 80px;
        font-size: 27px;
        line-height: 1.15;
    }
}

.description {
    @extend %p;

    margin: -120px 0 120px 0;
    font-size: 16px;
    line-height: 1.5;
    color: $c-text-secondary;
    text-align: left;

    @include mobileOnly {
        margin: -60px 0 60px 0;
        font-size: 14px;
    }
}

// The primary button is BaseButton's own L pill, hugging its label (T8) — no local
// geometry. The stack's gap separates the CTA from the secondary link.
.actions {
    display: flex;
    flex-direction: column;
    gap: $space-24;
    align-items: center;
    width: 100%;
    margin-top: $s-xs;

    @include mobileOnly {
        flex-direction: row;
        flex-wrap: wrap;
        gap: $s-m;
        align-items: center;
        justify-content: center;
        margin-top: $s-xl;
    }
}

.secondary-link {
    @extend %p;

    align-self: center;
    font-size: 15px;
    font-weight: 400;
    color: $c-text-secondary;
    text-align: center;
    letter-spacing: 0.01em;
    text-decoration: none;
    cursor: pointer;
    background: transparent;
    border: none;

    @include transition((color, background-color), fast);

    @include mobileOnly {
        flex-shrink: 0;
        align-self: center;
        padding: 0 $s-xs;
        margin-top: 0;
        font-size: 14px;
        white-space: nowrap;
    }

    &:hover {
        font-weight: 700;
        color: $c-text-primary;
    }
}
</style>
